import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  cn,
  Flex,
  Icon,
  Item,
  ItemGroup,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  type IconType,
} from '@kognitos/lattice';
import type { V1Run, V1RunContentMatch } from '@/generated/hooks/jeeves/models';
import {
  getContentCoverageSummary,
  getContentMatchSourceLabel,
  getContentMatchSummary,
  getFileMatchContext,
  getFileMatchLocations,
  getFileMatchName,
  getHiddenFileMatchCount,
  keyFileMatches,
  type IContentCoverageSummary,
} from '../content-search';

/**
 * A compact link-style button in a runs-table cell that opens a popover.
 * `statsig-no-capture` keeps its text, which can be a file name, out of
 * Statsig's click auto-capture.
 */
function CellPopover({
  icon,
  label,
  muted,
  testId,
  children,
}: {
  icon: IconType;
  label: string;
  muted?: boolean;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="xs"
          className={cn(
            'statsig-no-capture h-auto max-w-full justify-start gap-1 px-0',
            muted && 'text-muted-foreground',
          )}
          onClick={event => event.stopPropagation()}
          data-testid={`${testId}-trigger`}
        >
          <Icon type={icon} size="xs" className="shrink-0" />
          <span className="min-w-0 truncate">{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className="max-h-[var(--radix-popover-content-available-height)] w-80 overflow-y-auto"
        onClick={event => event.stopPropagation()}
        data-testid={testId}
        // Session replays mask the file names; the popover renders outside the runs table.
        data-sentry-mask
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Says which files a run matched the search term in, and where in them. Lists
 * file names and locations only, never the matched text.
 *
 * @param props.match - The run's entry in ListRuns `content_matches`
 * @returns The indicator, or null when the match lists no files
 */
export function ContentMatchIndicator({ match }: { match: V1RunContentMatch }) {
  const { t } = useTranslation('automation');
  const summary = getContentMatchSummary(match);
  if (!summary) return null;
  const hidden = getHiddenFileMatchCount(match);
  const files = keyFileMatches(match.file_matches);

  return (
    <CellPopover icon="FileText" label={summary} testId="run-content-matches">
      <Flex direction="column" gap={3}>
        <Text level="small" weight="medium">
          {t('automation.runs.contentSearch.matchesTitle')}
        </Text>
        <ItemGroup className="gap-3">
          {files.map(({ file, key }) => {
            const details = [
              getFileMatchContext(file),
              ...getFileMatchLocations(file),
            ].filter(Boolean);
            return (
              <Item
                key={key}
                role="listitem"
                size="sm"
                // Item wraps by default; a wrapping column grows to its widest
                // child, so a long file name would never truncate.
                className="flex-col flex-nowrap items-stretch gap-0.5 p-0"
                data-testid="run-content-match"
              >
                <Flex align="center" justify="between" gap={2}>
                  <Text
                    level="small"
                    // The tooltip renders outside the popover, so it masks its own copy.
                    ellipsis={{
                      tooltip: (
                        <span data-sentry-mask>{getFileMatchName(file)}</span>
                      ),
                    }}
                    className="min-w-0 flex-1"
                  >
                    {getFileMatchName(file)}
                  </Text>
                  <Badge variant="outline" className="ml-auto shrink-0">
                    {getContentMatchSourceLabel(file.source)}
                  </Badge>
                </Flex>
                {details.length > 0 && (
                  <Text level="xSmall" color="muted" className="break-words">
                    {details.join(' · ')}
                  </Text>
                )}
              </Item>
            );
          })}
        </ItemGroup>
        {hidden > 0 && (
          <Text level="xSmall" color="muted">
            {t('automation.runs.contentSearch.moreFiles', { count: hidden })}
          </Text>
        )}
      </Flex>
    </CellPopover>
  );
}

/**
 * Flags a run whose files aren't all searchable ("2 of 3 files searchable"),
 * with why, so a search that misses the run isn't a silent miss.
 *
 * @param props.summary - The run's coverage summary
 * @returns The note
 */
export function ContentCoverageNote({
  summary,
}: {
  summary: IContentCoverageSummary;
}) {
  const { t } = useTranslation('automation');
  return (
    <CellPopover
      icon="FileScan"
      label={summary.label}
      muted
      testId="run-content-coverage"
    >
      <Flex direction="column" gap={3}>
        <Flex direction="column" gap={0.5}>
          <Text level="small" weight="medium">
            {summary.label}
          </Text>
          <Text level="xSmall" color="muted">
            {t('automation.runs.contentSearch.coverage.description')}
          </Text>
        </Flex>
        <ItemGroup className="gap-1.5">
          {summary.lines.map(line => (
            <Item
              key={line.key}
              role="listitem"
              size="sm"
              className="flex-nowrap items-start justify-between gap-3 p-0"
            >
              <Text level="small">{line.label}</Text>
              <Text level="small" className="tabular-nums">
                {line.count}
              </Text>
            </Item>
          ))}
        </ItemGroup>
      </Flex>
    </CellPopover>
  );
}

/**
 * A run's file-content search details for the runs list: where an active
 * search matched inside its files, and which of its files can't be searched.
 *
 * @param props.run - The run
 * @param props.match - The run's entry in ListRuns `content_matches`, if any
 * @returns The details, or null when there are none
 */
export function RunContentSearchDetails({
  run,
  match,
}: {
  run: V1Run | undefined;
  match: V1RunContentMatch | undefined;
}) {
  const coverage = getContentCoverageSummary(run?.content_coverage);
  const hasMatch = Boolean(getContentMatchSummary(match));
  if (!hasMatch && !coverage) return null;

  return (
    <Flex
      direction="column"
      align="start"
      gap={0.5}
      width="full"
      className="min-w-0"
      data-testid="run-content-search-details"
      data-sentry-mask
    >
      {match && hasMatch && <ContentMatchIndicator match={match} />}
      {coverage && <ContentCoverageNote summary={coverage} />}
    </Flex>
  );
}
